import { RpcError, StatusCode } from "grpc-web";

/**
 * The guest "terms required" gate. The backend returns
 * FAILED_PRECONDITION/"terms_required" on any upload RPC for a guest who has
 * not yet accepted Terms of Use + Privacy Policy. Rather than gate every UI
 * entry point proactively, we let those errors propagate up and intercept
 * them at the service layer: a wrapper around each guest-uploadable RPC
 * catches the error, opens a consent modal mounted globally at the app shell,
 * and on accept retries the original action verbatim.
 *
 * Cross-layer plumbing: the GuestTermsGateProvider React component registers
 * a Promise-returning requester via setGuestTermsConsentRequester at mount.
 * The non-React service wrappers call requestGuestTermsConsent(), which
 * returns true once the user has accepted (the modal closes) or false if
 * they cancelled.
 */

type ConsentRequester = () => Promise<boolean>;

let requester: ConsentRequester | null = null;

export const setGuestTermsConsentRequester = (
  fn: ConsentRequester | null
): void => {
  requester = fn;
};

export const requestGuestTermsConsent = async (): Promise<boolean> => {
  if (!requester) return false;
  return requester();
};

export const isTermsRequiredError = (err: unknown): boolean => {
  if (!err) return false;
  const e = err as Partial<RpcError>;
  return (
    e.code === StatusCode.FAILED_PRECONDITION && e.message === "terms_required"
  );
};

/**
 * Wrap a guest-uploadable RPC call so a FAILED_PRECONDITION/terms_required
 * error from the backend opens the consent modal and, on accept, retries
 * the call once. Any other error propagates unchanged.
 */
export const withGuestTermsGate = async <T>(
  action: () => Promise<T>
): Promise<T> => {
  try {
    return await action();
  } catch (err) {
    if (!isTermsRequiredError(err)) throw err;
    const accepted = await requestGuestTermsConsent();
    if (!accepted) throw err;
    return await action();
  }
};
